import type { ChangeEvent, FC } from 'react'
import { useCallback, useEffect, useRef, useState } from 'react'

import { File } from '@oneToOne/shared/assets/File'
import { Picture } from '@oneToOne/shared/assets/Picture'

import { UnstyledButton } from '@core/components/twind/Button/Button'
import { useContextTranslation } from '@core/i18n'
import { useTwindContext } from '@core/twind'
import { isMobileWeb } from '@core/userAgent'

import {
  menuButtonMenu,
  menuButtonMenuItem,
  menuButtonPlus,
  menuButtonTrigger,
} from './UploadButton.twind'

interface UploadButtonProps {
  handleFileChange: (e: ChangeEvent<HTMLInputElement>) => void
}

/**
 * Upload file button for chat input
 */
export const UploadButton: FC<UploadButtonProps> = ({ handleFileChange }) => {
  const { tw } = useTwindContext()
  const { t } = useContextTranslation()
  const [isOpen, setIsOpen] = useState(false)
  const menuRef = useRef<HTMLUListElement>(null)
  const fileInputRef = useRef<HTMLInputElement>(null)

  const configureFileInput = useCallback((accept: string, capture?: string) => {
    fileInputRef.current?.setAttribute('accept', accept)
    if (capture) {
      fileInputRef.current?.setAttribute('capture', capture)
    } else {
      fileInputRef.current?.removeAttribute('capture')
    }
  }, [])

  const handleClick = useCallback(() => {
    if (isMobileWeb()) {
      setIsOpen(true)
    } else {
      configureFileInput('*')
      fileInputRef.current?.click()
    }
  }, [configureFileInput])

  const handleMenuClick = useCallback(
    (accept: string, capture: string | undefined) => {
      configureFileInput(accept, capture)
      fileInputRef.current?.click()
      setIsOpen(false)
    },
    [configureFileInput],
  )

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (menuRef.current && !menuRef.current.contains(event.target as Node)) {
        // Our Modal loads async, so we need to debounce the close
        setTimeout(() => {
          setIsOpen(false)
        }, 300)
      }
    }

    document.addEventListener('mousedown', handleClickOutside)

    return () => {
      document.removeEventListener('mousedown', handleClickOutside)
    }
  }, [])

  const options = [
    {
      label: t('File / Media'),
      icon: <File width="20px" height="20px" color="#A142FF" label="file" />,
      accept: 'image/*, video/*',
      capture: undefined,
    },
    {
      label: t('Camera'),
      icon: (
        <Picture width="20px" height="20px" color="#428BFF" label="picture" />
      ),
      accept: 'image/*',
      capture: 'environment',
    },
  ]

  return (
    <div className={tw?.('relative')}>
      {isOpen ? (
        <ul className={tw?.(menuButtonMenu)} ref={menuRef} role="menu">
          {options.map(({ label, icon, accept, capture }) => (
            <li
              className={tw?.(menuButtonMenuItem)}
              key={label}
              onClick={() => {
                handleMenuClick(accept, capture)
              }}
              aria-hidden
            >
              {icon} {label}
            </li>
          ))}
        </ul>
      ) : null}
      <UnstyledButton overrideCSS={menuButtonTrigger} onClick={handleClick}>
        <span className={tw?.(menuButtonPlus)}>&#43;</span>
      </UnstyledButton>
      <input
        ref={fileInputRef}
        type="file"
        style={{ display: 'none' }}
        onChange={handleFileChange}
      />
    </div>
  )
}
